/**
 * Políticas de Trabalho — grupos de regras CLT/CCT aplicáveis a funcionários.
 * Organizado em abas: Jornada | Extras | Noturno | DSR | In Itinere | Banco de Horas
 */
import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { politicaApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { PoliticaTrabalho } from '../../types/api'

const DEFAULT_FORM: Omit<PoliticaTrabalho, 'id' | 'ativo' | 'created_at'> = {
  nome: '',
  jornada_horas_dia: 8,
  jornada_horas_semana: 44,
  tolerancia_entrada_min: 5,
  tolerancia_saida_min: 5,
  intervalo_min_minutos: 60,
  intervalo_deducao_automatica: false,
  intervalo_deducao_minutos: 60,
  extra_pct_dia_util: 50,
  extra_pct_feriado: 100,
  extra_pct_dsf: 100,
  extra_limite_diario_min: 120,
  noturno_habilitado: true,
  noturno_percentual: 20,
  noturno_inicio: '22:00',
  noturno_fim: '05:00',
  noturno_hora_reduzida: true,
  dsr_habilitado: true,
  dsr_dia_semana: 6,
  dsr_proporcional_faltas: true,
  itinere_habilitado: false,
  itinere_minutos_ida: 0,
  itinere_minutos_volta: 0,
  itinere_base_legal: '',
  banco_horas_habilitado: false,
  banco_horas_tipo: 'INDIVIDUAL',
  banco_horas_prazo_meses: 6,
  banco_horas_limite_positivo_h: 40,
  banco_horas_limite_negativo_h: 20,
}

const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb']

type FormState = typeof DEFAULT_FORM
type Tab = 'jornada' | 'extras' | 'noturno' | 'dsr' | 'itinere' | 'banco'

export function PoliticasPage() {
  const qc = useQueryClient()
  const { data: politicas, isLoading } = useQuery({ queryKey: ['politicas'], queryFn: politicaApi.list })

  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const [tab, setTab] = useState<Tab>('jornada')
  const [formError, setFormError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = { ...form, itinere_base_legal: form.itinere_base_legal || null }
      return editId ? politicaApi.update(editId, payload) : politicaApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['politicas'] }); closeForm() },
    onError: err => setFormError(getErrorMessage(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => politicaApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['politicas'] }),
  })

  const clonarMutation = useMutation({
    mutationFn: (id: string) => politicaApi.clonar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['politicas'] }),
  })

  function openCreate() { setEditId(null); setForm(DEFAULT_FORM); setFormError(null); setTab('jornada'); setShowForm(true) }
  function openEdit(p: PoliticaTrabalho) {
    setEditId(p.id)
    setForm({ ...p })
    setFormError(null); setTab('jornada'); setShowForm(true)
  }
  function closeForm() { setShowForm(false); setEditId(null) }

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>Políticas de Trabalho</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Grupos de regras CLT/CCT — jornada, extras, noturno, DSR, in itinere, banco de horas
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nova Política</button>
      </div>

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 'var(--space-4)' }}
          onClick={e => { if (e.target === e.currentTarget) closeForm() }}>
          <div className="card" style={{ width: '100%', maxWidth: 620, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
            <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)', flexShrink: 0 }}>
              {editId ? 'Editar Política' : 'Nova Política de Trabalho'}
            </h2>
            {formError && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-3)', flexShrink: 0 }}>{formError}</div>}

            <div style={{ marginBottom: 'var(--space-4)', flexShrink: 0 }}>
              <F label="Nome da política">
                <input className="input" value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Ex: Administrativo CLT, Turno Noite" required />
              </F>
            </div>

            {/* Abas */}
            <div style={{ display: 'flex', borderBottom: '2px solid var(--color-gray-200)', marginBottom: 'var(--space-4)', flexShrink: 0, overflowX: 'auto' }}>
              {([['jornada','Jornada'],['extras','Horas Extras'],['noturno','Noturno'],['dsr','D.S.R.'],['itinere','In Itinere'],['banco','Banco Horas']] as [Tab, string][]).map(([t, l]) => (
                <button key={t} onClick={() => setTab(t)} style={{
                  padding: 'var(--space-2) var(--space-3)', border: 'none', background: 'none', cursor: 'pointer',
                  fontWeight: tab === t ? 700 : 400, fontSize: 'var(--font-size-sm)',
                  borderBottom: tab === t ? '2px solid var(--color-primary)' : '2px solid transparent',
                  color: tab === t ? 'var(--color-primary)' : 'var(--color-gray-600)', whiteSpace: 'nowrap',
                }}>{l}</button>
              ))}
            </div>

            <form onSubmit={(e: FormEvent) => { e.preventDefault(); saveMutation.mutate() }} style={{ flex: 1, overflowY: 'auto' }}>

              {/* ABA: Jornada */}
              {tab === 'jornada' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                  <F label="Horas/dia (h)">
                    <input className="input" type="number" min={1} max={12} step={0.5} value={form.jornada_horas_dia} onChange={e => set('jornada_horas_dia', Number(e.target.value))} />
                  </F>
                  <F label="Horas/semana (h)">
                    <input className="input" type="number" min={4} max={60} value={form.jornada_horas_semana} onChange={e => set('jornada_horas_semana', Number(e.target.value))} />
                  </F>
                  <F label="Tolerância entrada (min)">
                    <input className="input" type="number" min={0} max={30} value={form.tolerancia_entrada_min} onChange={e => set('tolerancia_entrada_min', Number(e.target.value))} />
                  </F>
                  <F label="Tolerância saída (min)">
                    <input className="input" type="number" min={0} max={30} value={form.tolerancia_saida_min} onChange={e => set('tolerancia_saida_min', Number(e.target.value))} />
                  </F>
                  <div style={{ gridColumn: '1/-1' }}>
                    <Note>Art. 58 §1° CLT — tolerância de até 5 min não computada como extra ou falta.</Note>
                  </div>
                  <F label="Intervalo mínimo (min) — art. 71">
                    <input className="input" type="number" min={15} max={120} value={form.intervalo_min_minutos} onChange={e => set('intervalo_min_minutos', Number(e.target.value))} />
                  </F>
                  <F label="Dedução de intervalo (min)" opt>
                    <input className="input" type="number" min={0} max={120} value={form.intervalo_deducao_minutos} onChange={e => set('intervalo_deducao_minutos', Number(e.target.value))} disabled={!form.intervalo_deducao_automatica} />
                  </F>
                  <div style={{ gridColumn: '1/-1' }}>
                    <CheckField label="Deduzir intervalo automaticamente" checked={form.intervalo_deducao_automatica} onChange={v => set('intervalo_deducao_automatica', v)} />
                  </div>
                </div>
              )}

              {/* ABA: Extras */}
              {tab === 'extras' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                  <F label="Extra — dia útil (%)">
                    <input className="input" type="number" min={50} max={200} value={form.extra_pct_dia_util} onChange={e => set('extra_pct_dia_util', Number(e.target.value))} />
                  </F>
                  <F label="Extra — feriado (%)">
                    <input className="input" type="number" min={50} max={200} value={form.extra_pct_feriado} onChange={e => set('extra_pct_feriado', Number(e.target.value))} />
                  </F>
                  <F label="Extra — DSR trabalhado (%)">
                    <input className="input" type="number" min={50} max={200} value={form.extra_pct_dsf} onChange={e => set('extra_pct_dsf', Number(e.target.value))} />
                  </F>
                  <F label="Limite diário de extras (min)">
                    <input className="input" type="number" min={60} max={480} step={30} value={form.extra_limite_diario_min} onChange={e => set('extra_limite_diario_min', Number(e.target.value))} />
                  </F>
                  <div style={{ gridColumn: '1/-1' }}>
                    <Note>Art. 59 CLT — mínimo 50% no dia útil, 100% em domingos/feriados. Limite: 2h/dia (ACT pode alterar).</Note>
                  </div>
                </div>
              )}

              {/* ABA: Noturno */}
              {tab === 'noturno' && (
                <div>
                  <CheckField label="Habilitar adicional noturno (art. 73 CLT)" checked={form.noturno_habilitado} onChange={v => set('noturno_habilitado', v)} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginTop: 'var(--space-4)', opacity: form.noturno_habilitado ? 1 : 0.5 }}>
                    <F label="Adicional noturno (%)">
                      <input className="input" type="number" min={20} max={50} value={form.noturno_percentual} onChange={e => set('noturno_percentual', Number(e.target.value))} disabled={!form.noturno_habilitado} />
                    </F>
                    <div />
                    <F label="Início do período noturno">
                      <input className="input" type="time" value={form.noturno_inicio} onChange={e => set('noturno_inicio', e.target.value)} disabled={!form.noturno_habilitado} />
                    </F>
                    <F label="Fim do período noturno">
                      <input className="input" type="time" value={form.noturno_fim} onChange={e => set('noturno_fim', e.target.value)} disabled={!form.noturno_habilitado} />
                    </F>
                    <div style={{ gridColumn: '1/-1' }}>
                      <CheckField label="Hora noturna reduzida (52min30s = 1h — art. 73 §1°)" checked={form.noturno_hora_reduzida} onChange={v => set('noturno_hora_reduzida', v)} disabled={!form.noturno_habilitado} />
                    </div>
                    <div style={{ gridColumn: '1/-1' }}>
                      <Note>Padrão: 22h–5h, 20% adicional. Agricultura: 21h–5h, 25%. Petroleo: diferente. Ajuste conforme ACT/CCT.</Note>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA: DSR */}
              {tab === 'dsr' && (
                <div>
                  <CheckField label="Habilitar D.S.R. (Lei 605/1949)" checked={form.dsr_habilitado} onChange={v => set('dsr_habilitado', v)} />
                  <div style={{ marginTop: 'var(--space-4)', opacity: form.dsr_habilitado ? 1 : 0.5 }}>
                    <F label="Dia do D.S.R.">
                      <select className="input" value={form.dsr_dia_semana} onChange={e => set('dsr_dia_semana', Number(e.target.value))} disabled={!form.dsr_habilitado}>
                        {DIAS_SEMANA.map((d, i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    </F>
                    <CheckField label="D.S.R. proporcional a faltas injustificadas" checked={form.dsr_proporcional_faltas} onChange={v => set('dsr_proporcional_faltas', v)} disabled={!form.dsr_habilitado} />
                    <div style={{ marginTop: 'var(--space-4)' }}>
                      <Note>Lei 605/1949 — 1 dia de descanso por semana. D.S.R. é proporcional se houver falta injustificada na semana.</Note>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA: In Itinere */}
              {tab === 'itinere' && (
                <div>
                  <CheckField label="Habilitar horas in itinere" checked={form.itinere_habilitado} onChange={v => set('itinere_habilitado', v)} />
                  <div style={{ marginTop: 'var(--space-4)', opacity: form.itinere_habilitado ? 1 : 0.5 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                      <F label="Tempo de percurso — ida (min)">
                        <input className="input" type="number" min={0} max={240} value={form.itinere_minutos_ida} onChange={e => set('itinere_minutos_ida', Number(e.target.value))} disabled={!form.itinere_habilitado} />
                      </F>
                      <F label="Tempo de percurso — volta (min)">
                        <input className="input" type="number" min={0} max={240} value={form.itinere_minutos_volta} onChange={e => set('itinere_minutos_volta', Number(e.target.value))} disabled={!form.itinere_habilitado} />
                      </F>
                    </div>
                    <F label="Base legal" opt>
                      <input className="input" value={form.itinere_base_legal ?? ''} onChange={e => set('itinere_base_legal', e.target.value)} placeholder="Ex: Art. 58 §2° CLT / ACT 2024 Cláusula X" disabled={!form.itinere_habilitado} />
                    </F>
                    <Note>Reforma Trabalhista 2017 (Lei 13.467) revogou in itinere obrigatório, mas ACT/CCT pode manter. Documente a base legal.</Note>
                  </div>
                </div>
              )}

              {/* ABA: Banco de Horas */}
              {tab === 'banco' && (
                <div>
                  <CheckField label="Habilitar banco de horas" checked={form.banco_horas_habilitado} onChange={v => set('banco_horas_habilitado', v)} />
                  <div style={{ marginTop: 'var(--space-4)', opacity: form.banco_horas_habilitado ? 1 : 0.5 }}>
                    <F label="Modalidade">
                      <select className="input" value={form.banco_horas_tipo} onChange={e => set('banco_horas_tipo', e.target.value)} disabled={!form.banco_horas_habilitado}>
                        <option value="INDIVIDUAL">Individual — art. 59 §6° CLT (sem CCT, prazo 6 meses)</option>
                        <option value="COLETIVO">Coletivo — art. 59 §2° CLT (via ACT/CCT, prazo 12 meses)</option>
                      </select>
                    </F>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
                      <F label="Prazo de compensação (meses)">
                        <input className="input" type="number" min={1} max={12} value={form.banco_horas_prazo_meses} onChange={e => set('banco_horas_prazo_meses', Number(e.target.value))} disabled={!form.banco_horas_habilitado} />
                      </F>
                      <F label="Limite crédito (horas)">
                        <input className="input" type="number" min={0} max={400} value={form.banco_horas_limite_positivo_h} onChange={e => set('banco_horas_limite_positivo_h', Number(e.target.value))} disabled={!form.banco_horas_habilitado} />
                      </F>
                      <F label="Limite débito (horas)">
                        <input className="input" type="number" min={0} max={200} value={form.banco_horas_limite_negativo_h} onChange={e => set('banco_horas_limite_negativo_h', Number(e.target.value))} disabled={!form.banco_horas_habilitado} />
                      </F>
                    </div>
                    <Note>Individual (§6°): acordado individualmente, prazo 6 meses. Coletivo (§2°): via ACT/CCT, prazo até 1 ano.</Note>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-200)' }}>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? <PageLoading /> : !politicas?.length ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ color: 'var(--color-gray-400)', marginBottom: 'var(--space-4)' }}>Nenhuma política cadastrada.</p>
          <button className="btn btn-primary" onClick={openCreate}>Criar primeira política</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'var(--space-4)' }}>
          {politicas.map((p: PoliticaTrabalho) => (
            <div key={p.id} className="card" style={{ opacity: p.ativo ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)' }}>{p.nome}</div>
                  {!p.ativo && <span className="badge badge-gray">Inativa</span>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)' }}>
                <Kv k="Jornada" v={`${p.jornada_horas_dia}h/dia · ${p.jornada_horas_semana}h/sem`} />
                <Kv k="Tolerância" v={`${p.tolerancia_entrada_min}min entrada / ${p.tolerancia_saida_min}min saída`} />
                <Kv k="Extras" v={`${p.extra_pct_dia_util}% útil / ${p.extra_pct_feriado}% feriado`} />
                <Kv k="Noturno" v={p.noturno_habilitado ? `${p.noturno_percentual}% (${p.noturno_inicio}–${p.noturno_fim})` : 'Desab.'} />
                <Kv k="Intervalo" v={`${p.intervalo_min_minutos}min mín.${p.intervalo_deducao_automatica ? ' · auto-dedução' : ''}`} />
                <Kv k="D.S.R." v={p.dsr_habilitado ? (p.dsr_proporcional_faltas ? 'proporcional' : 'integral') : 'Desab.'} />
                {p.itinere_habilitado && <Kv k="In itinere" v={`${p.itinere_minutos_ida + p.itinere_minutos_volta}min/dia`} />}
                {p.banco_horas_habilitado && <Kv k="Banco horas" v={`${p.banco_horas_tipo} ${p.banco_horas_prazo_meses}m`} />}
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-100)' }}>
                <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => openEdit(p)}>Editar</button>
                <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => clonarMutation.mutate(p.id)}>Duplicar</button>
                <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                  onClick={() => { if (confirm(`Remover "${p.nome}"?`)) deleteMutation.mutate(p.id) }}>Remover</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Kv({ k, v }: { k: string; v: string }) {
  return <div><span style={{ color: 'var(--color-gray-400)' }}>{k}: </span>{v}</div>
}

function CheckField({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: disabled ? 'not-allowed' : 'pointer', marginBottom: 'var(--space-3)', opacity: disabled ? 0.5 : 1 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
      <span style={{ fontSize: 'var(--font-size-sm)' }}>{label}</span>
    </label>
  )
}

function Note({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', padding: 'var(--space-2) var(--space-3)', backgroundColor: 'var(--color-gray-50)', borderRadius: 4, marginTop: 'var(--space-2)' }}>ℹ {children}</p>
}

function F({ label, children, opt }: { label: string; children: React.ReactNode; opt?: boolean }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
        {label}{opt && <span style={{ color: 'var(--color-gray-400)', fontWeight: 400 }}> (opcional)</span>}
      </label>
      {children}
    </div>
  )
}
